import React, { useState, useEffect } from 'react';
import { TicketCategory, TicketSite } from '../../entities/ticket/model/types';
import { getCategories, createCategory, updateCategory, deleteCategory } from '../../entities/category/api/categoryApi';
import * as S from './CategoriesPage.styles';

export const CategoriesPage: React.FC = () => {
  const [categories, setCategories] = useState<TicketCategory[]>([]);
  const [filteredCategories, setFilteredCategories] = useState<TicketCategory[]>([]);
  const [selectedSite, setSelectedSite] = useState<TicketSite | 'all'>('all');
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [editingCategory, setEditingCategory] = useState<TicketCategory | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    site: 'hudle' as TicketSite,
    parentId: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    if (selectedSite === 'all') {
      setFilteredCategories(categories);
    } else {
      setFilteredCategories(categories.filter(cat => cat.site === selectedSite));
    }
  }, [categories, selectedSite]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const data = await getCategories();
      setCategories(data);
    } catch (error) {
      console.error('Ошибка загрузки категорий:', error);
      alert('Не удалось загрузить категории');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = () => {
    setEditingCategory(null);
    setFormData({ name: '', site: 'hudle', parentId: '' });
    setShowAddCategory(true);
  };

  const handleEditCategory = (category: TicketCategory) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      site: category.site || 'hudle',
      parentId: category.parentId || '',
    });
    setShowAddCategory(true);
  };

  const handleSaveCategory = async () => {
    if (!formData.name || !formData.site) {
      alert('Заполните все обязательные поля');
      return;
    }

    try {
      setLoading(true);
      if (editingCategory) {
        await updateCategory(editingCategory.id, {
          name: formData.name,
          site: formData.site,
          parentId: formData.parentId || undefined,
        });
      } else {
        await createCategory({
          name: formData.name,
          site: formData.site,
          parentId: formData.parentId || undefined,
        });
      }
      await loadCategories();
      setShowAddCategory(false);
      setFormData({ name: '', site: 'hudle', parentId: '' });
    } catch (error) {
      console.error('Ошибка сохранения категории:', error);
      alert('Не удалось сохранить категорию');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (id: string) => {
    if (!window.confirm('Удалить категорию?')) {
      return;
    }

    try {
      setLoading(true);
      await deleteCategory(id);
      await loadCategories();
    } catch (error) {
      console.error('Ошибка удаления категории:', error);
      alert('Не удалось удалить категорию');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryName = (categoryId?: string): string => {
    if (!categoryId) return '';
    const category = categories.find(cat => cat.id === categoryId);
    return category?.name || '';
  };

  const getSiteLabel = (site?: TicketSite): string => {
    if (site === 'hudle') return 'Hudle';
    if (site === 'unknown') return 'Неизвестен';
    return 'Турнир';
  };

  return (
    <S.CategoriesContainer>
      <S.CategoriesHeader>
        <S.CategoriesTitle>Категории</S.CategoriesTitle>
        <S.HeaderActions>
          <S.SiteFilter>
            <S.SiteFilterLabel>Сайт:</S.SiteFilterLabel>
            <S.SiteFilterSelect
              value={selectedSite}
              onChange={(e) => setSelectedSite(e.target.value as TicketSite | 'all')}
            >
              <option value="all">Все</option>
              <option value="hudle">Hudle</option>
              <option value="tournament">Турнир</option>
              <option value="unknown">Неизвестен</option>
            </S.SiteFilterSelect>
          </S.SiteFilter>
          <S.AddButton onClick={handleAddCategory} disabled={loading}>
            + Добавить категорию
          </S.AddButton>
        </S.HeaderActions>
      </S.CategoriesHeader>

      <S.CategoriesContent>
        {loading && categories.length === 0 ? (
          <S.EmptyState>
            <S.EmptyStateText>Загрузка...</S.EmptyStateText>
          </S.EmptyState>
        ) : filteredCategories.length === 0 ? (
          <S.EmptyState>
            <S.EmptyStateText>Нет категорий</S.EmptyStateText>
            <S.EmptyStateSubtext>
              Создайте категории для организации тикетов
            </S.EmptyStateSubtext>
          </S.EmptyState>
        ) : (
          <S.CategoriesList>
            {filteredCategories.map(category => (
              <S.CategoryCard key={category.id}>
                <S.CategoryCardHeader>
                  <S.CategoryCardName>{category.name}</S.CategoryCardName>
                  <S.CategoryCardSite>{getSiteLabel(category.site)}</S.CategoryCardSite>
                </S.CategoryCardHeader>
                {category.parentId && (
                  <S.CategoryCardParent>
                    Родительская категория: {getCategoryName(category.parentId)}
                  </S.CategoryCardParent>
                )}
                <S.CategoryCardActions>
                  <S.EditButton onClick={() => handleEditCategory(category)} disabled={loading}>
                    Редактировать
                  </S.EditButton>
                  <S.DeleteButton onClick={() => handleDeleteCategory(category.id)} disabled={loading}>
                    Удалить
                  </S.DeleteButton>
                </S.CategoryCardActions>
              </S.CategoryCard>
            ))}
          </S.CategoriesList>
        )}
      </S.CategoriesContent>

      {showAddCategory && (
        <S.ModalOverlay onClick={() => !loading && setShowAddCategory(false)}>
          <S.ModalContent onClick={(e) => e.stopPropagation()}>
            <S.ModalHeader>
              <S.ModalTitle>
                {editingCategory ? 'Редактировать категорию' : 'Создать категорию'}
              </S.ModalTitle>
              <S.CloseButton onClick={() => !loading && setShowAddCategory(false)} disabled={loading}>
                ×
              </S.CloseButton>
            </S.ModalHeader>

            <S.ModalBody>
              <S.FormGroup>
                <S.FormLabel>Название *</S.FormLabel>
                <S.FormInput
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Название категории"
                  disabled={loading}
                />
              </S.FormGroup>

              <S.FormGroup>
                <S.FormLabel>Сайт *</S.FormLabel>
                <S.FormSelect
                  value={formData.site}
                  onChange={(e) => setFormData({ ...formData, site: e.target.value as TicketSite })}
                  disabled={loading}
                >
                  <option value="hudle">Hudle</option>
                  <option value="tournament">Турнир</option>
                  <option value="unknown">Неизвестен</option>
                </S.FormSelect>
              </S.FormGroup>

              <S.FormGroup>
                <S.FormLabel>Родительская категория</S.FormLabel>
                <S.FormSelect
                  value={formData.parentId}
                  onChange={(e) => setFormData({ ...formData, parentId: e.target.value })}
                  disabled={loading}
                >
                  <option value="">Нет</option>
                  {categories
                    .filter(cat => cat.site === formData.site && cat.id !== editingCategory?.id)
                    .map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                </S.FormSelect>
              </S.FormGroup>
            </S.ModalBody>

            <S.ModalFooter>
              <S.CancelButton onClick={() => !loading && setShowAddCategory(false)} disabled={loading}>
                Отмена
              </S.CancelButton>
              <S.SaveButton onClick={handleSaveCategory} disabled={loading}>
                {loading ? 'Сохранение...' : 'Сохранить'}
              </S.SaveButton>
            </S.ModalFooter>
          </S.ModalContent>
        </S.ModalOverlay>
      )}
    </S.CategoriesContainer>
  );
};

